import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { companyInfo } from '../mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/products', label: 'Products' },
    { path: '/applications', label: 'Applications' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact Us' }
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}
    >
      {/* Top Contact Bar */}
      <div className={`bg-brand text-white transition-all duration-300 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-auto opacity-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
          <div className="flex justify-center sm:justify-end items-center gap-4 sm:gap-6 text-xs sm:text-sm font-roboto">
            <a href={`tel:${companyInfo.phone}`} data-testid="header-phone" className="flex items-center gap-1.5 hover:text-brand-50 transition-colors">
              <Phone size={12} />
              <span className="hidden sm:inline">{companyInfo.phone}</span>
              <span className="sm:hidden">Call Us</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} data-testid="header-email" className="flex items-center gap-1.5 hover:text-brand-50 transition-colors">
              <Mail size={12} />
              <span>{companyInfo.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo / Brand */}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-10 sm:h-14 lg:h-[68px] w-auto flex-shrink-0"
            />
            <span className="font-oswald font-bold text-sm sm:text-lg md:text-2xl lg:text-[28px] text-slate-800 tracking-wide group-hover:text-brand transition-colors uppercase truncate">
              SYNERGYTUFF INDIA PRIVATE LIMITED
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 xl:px-4 py-2 font-roboto text-sm font-medium transition-colors relative whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-brand'
                    : 'text-slate-700 hover:text-brand'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0 left-3 right-3 xl:left-4 xl:right-4 h-0.5 bg-brand"></span>
                )}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-700 hover:text-brand transition-colors flex-shrink-0"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-xl">
            <div className="px-4 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-roboto text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-brand-50 text-brand'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-brand'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
